import React, { useState, useEffect } from 'react';
import { User, Entrepreneur, Investor } from '../types';
import { 
  User as UserIcon, 
  Mail, 
  Sparkles, 
  Building2, 
  TrendingUp,
  FileText,
  MapPin,
  Phone,
  Globe,
  Save
} from 'lucide-react';
import { Input } from './design-system/Input';
import { Button } from './design-system/Button';
import { PillButton } from './design-system/PillButton';
import { Card, CardHeader, CardContent } from './design-system/Card';
import { Heading2, Body, BodySmall } from './design-system/Typography';
import { ImageUpload } from './ImageUpload';
import { SupabaseService } from '../services/supabaseService';

interface EnhancedUserProfileSetupProps {
  onComplete: (user: User) => void;
  initialUserData?: User;
  onLogout?: () => void;
}

interface FormData {
  name: string;
  email: string;
  bio: string;
  location: string;
  phone: string;
  website: string;
  profileImage?: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  bio?: string;
  location?: string;
  phone?: string;
  website?: string;
  userType?: string;
}

/**
 * Enhanced User Profile Setup Component
 * Features: Complete form validation, image upload, responsive design, profile editing
 */
export const EnhancedUserProfileSetup: React.FC<EnhancedUserProfileSetupProps> = ({ 
  onComplete,
  initialUserData,
  onLogout
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    bio: '',
    location: '',
    phone: '',
    website: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [selectedUserType, setSelectedUserType] = useState<'entrepreneur' | 'investor' | null>(
    initialUserData?.userType || null
  );

  // Initialize form with existing user data if editing
  useEffect(() => {
    if (initialUserData) {
     console.log('🔄 Inicializando formulario con datos existentes:', {
       name: initialUserData.name,
       email: initialUserData.email,
       location: initialUserData.location,
       phone: initialUserData.phone,
       website: initialUserData.website,
       bio: initialUserData.bio
     });
     
      setFormData({
        name: initialUserData.name || '',
       email: initialUserData.email || 'demo@email.com',
       bio: initialUserData.bio || 'Profesional apasionado por la innovación y el crecimiento empresarial.',
        location: initialUserData.location || '',
        phone: initialUserData.phone || '',
        website: initialUserData.website || '',
        profileImage: initialUserData.images?.[0] || undefined
      });
     
     // También actualizar el tipo de usuario seleccionado
     setSelectedUserType(initialUserData.userType);
     
     console.log('✅ Formulario inicializado con datos del usuario');
    }
  }, [initialUserData]);

  /**
   * Validates individual form fields
   */
  const validateField = (name: keyof FormData, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'El nombre es requerido';
        if (value.trim().length < 2) return 'El nombre debe tener al menos 2 caracteres';
        if (value.trim().length > 50) return 'El nombre no puede exceder 50 caracteres';
        break;

      case 'email':
        if (!value.trim()) return 'El email es requerido';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Por favor ingresa un email válido';
        break;

      case 'bio':
        if (value.length > 500) return 'La biografía no puede exceder 500 caracteres';
        break;

      case 'phone':
        if (value && !/^[\+]?[0-9\s\-\(\)]{9,15}$/.test(value)) {
          return 'Formato de teléfono inválido';
        }
        break;

      case 'website':
        if (value && !/^https?:\/\/.+\..+/.test(value)) {
          return 'URL inválida (debe incluir http:// o https://)';
        }
        break;

      case 'location':
        if (value.length > 100) return 'La ubicación no puede exceder 100 caracteres';
        break;
    }
    return undefined;
  };

  /**
   * Validates entire form
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // Validate required fields
    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'name' || key === 'email') {
        const error = validateField(key as keyof FormData, value);
        if (error) {
          newErrors[key as keyof FormErrors] = error;
          isValid = false;
        }
      }
    });

    // Validate optional fields if they have values
    ['bio', 'location', 'phone', 'website'].forEach(key => {
      const value = formData[key as keyof FormData];
      if (value) {
        const error = validateField(key as keyof FormData, value);
        if (error) {
          newErrors[key as keyof FormErrors] = error;
          isValid = false;
        }
      }
    });

    // Validate user type selection
    if (!selectedUserType) {
      newErrors.userType = 'Debes seleccionar si eres emprendedor o inversor';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  /**
   * Handles form field changes with real-time validation
   */
  const handleFieldChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Real-time validation for current field
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  /**
   * Handles user type selection
   */
  const handleUserTypeSelect = (userType: 'entrepreneur' | 'investor') => {
    setSelectedUserType(userType);
    // Clear user type error if it exists
    if (errors.userType) {
      setErrors(prev => ({ ...prev, userType: undefined }));
    }
  };
  /**
   * Handles image selection
   */
  const handleImageSelect = async (file: File, preview: string) => {
    console.log('📤 Iniciando subida de imagen de perfil...');
    setIsImageUploading(true);
    setImageUploadProgress(0);
    
    // Mostrar preview inmediatamente
    setFormData(prev => ({ ...prev, profileImage: preview }));
    
    try {
      // Simular progreso mientras se sube
      const progressInterval = setInterval(() => {
        setImageUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 20;
        });
      }, 200);
      
      // Generar ID temporal si no existe usuario
      const userId = initialUserData?.id || `temp-${Date.now()}`;
      
      // Subir imagen a Supabase Storage
      const uploadResult = await SupabaseService.uploadProfileImage(file, userId);
      
      clearInterval(progressInterval);
      setImageUploadProgress(100);
      
      if (uploadResult.success && uploadResult.url) {
        console.log('✅ Imagen subida exitosamente a Supabase Storage');
        console.log('🔗 URL de Supabase:', uploadResult.url);
        
        // Actualizar con la URL real de Supabase
        setFormData(prev => ({ ...prev, profileImage: uploadResult.url }));
        
        // Limpiar preview local
        URL.revokeObjectURL(preview);
        
        setTimeout(() => {
          setIsImageUploading(false);
          setImageUploadProgress(0);
        }, 1000);
        
      } else {
        console.error('❌ Error al subir imagen:', uploadResult.error);
        setErrors(prev => ({ 
          ...prev, 
          name: `Error al subir imagen: ${uploadResult.error}` 
        }));
        
        // Mantener preview local si falla la subida
        setIsImageUploading(false);
        setImageUploadProgress(0);
      }
      
    } catch (error) {
      console.error('❌ Error inesperado al subir imagen:', error);
      setErrors(prev => ({ 
        ...prev, 
        name: 'Error inesperado al subir la imagen. Inténtalo de nuevo.' 
      }));
      setIsImageUploading(false);
      setImageUploadProgress(0);
    }
  };

  /**
   * Handles image removal
   */
  const handleImageRemove = async () => {
    const currentImageUrl = formData.profileImage;
    
    // Limpiar imagen del formulario inmediatamente
    setFormData(prev => ({ ...prev, profileImage: undefined }));
    
    // Si la imagen está en Supabase Storage, intentar eliminarla
    if (currentImageUrl && currentImageUrl.includes('supabase')) {
      try {
        console.log('🗑️ Eliminando imagen de Supabase Storage...');
        const deleteResult = await SupabaseService.deleteProfileImage(currentImageUrl);
        
        if (deleteResult.success) {
          console.log('✅ Imagen eliminada de Supabase Storage');
        } else {
          console.warn('⚠️ No se pudo eliminar la imagen de Storage:', deleteResult.error);
        }
      } catch (error) {
        console.warn('⚠️ Error al eliminar imagen de Storage:', error);
      }
    } else if (currentImageUrl && currentImageUrl.startsWith('blob:')) {
      // Limpiar URL local si es un blob
      URL.revokeObjectURL(currentImageUrl);
    }
  };

  /**
   * Constructs user profile data based on form input and existing data
   */
  const constructUserProfile = (formData: FormData): User => {
    // If editing existing user, preserve ID and userType
    const userId = initialUserData?.id || `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const userType: 'entrepreneur' | 'investor' = selectedUserType || initialUserData?.userType || 'entrepreneur';
    const age = initialUserData?.age || Math.floor(Math.random() * (50 - 25 + 1)) + 25;

    // Use form location or default to random Spanish city
    const spanishCities = [
      'Madrid, España', 'Barcelona, España', 'Valencia, España', 'Sevilla, España',
      'Bilbao, España', 'Zaragoza, España', 'Málaga, España', 'Las Palmas, España'
    ];
    const location = formData.location || initialUserData?.location || spanishCities[Math.floor(Math.random() * spanishCities.length)];

    // Default professional images if no custom image uploaded
    const defaultImages = [
      'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop',
      'https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop',
      'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop'
    ];

    const images = formData.profileImage ? [formData.profileImage] : (initialUserData?.images || defaultImages);
    const bio = formData.bio || initialUserData?.bio || `Profesional apasionado por la innovación y el crecimiento empresarial.`;

    const baseUser = {
      id: userId,
      name: formData.name.trim(),
      age,
      location,
      images,
      bio,
      userType,
      email: formData.email.trim() || initialUserData?.email,
      phone: formData.phone.trim() || undefined,
      website: formData.website.trim() || undefined
    };

    if (userType === 'entrepreneur') {
      // Preserve existing entrepreneur data if editing
      const existingEntrepreneur = initialUserData?.userType === 'entrepreneur' ? initialUserData as Entrepreneur : null;
      
      const companies = ['TechFlow', 'InnovateHub', 'StartupLab', 'BusinessCore', 'VenturePoint'];
      const industries = ['SaaS', 'FinTech', 'HealthTech', 'EdTech', 'CleanTech'];
      const stages = ['idea', 'prototype', 'mvp', 'early-revenue', 'scaling'] as const;
      
      return {
        ...baseUser,
        userType: 'entrepreneur',
        company: existingEntrepreneur?.company || companies[Math.floor(Math.random() * companies.length)],
        industry: existingEntrepreneur?.industry || industries[Math.floor(Math.random() * industries.length)],
        stage: existingEntrepreneur?.stage || stages[Math.floor(Math.random() * stages.length)],
        fundingNeeded: existingEntrepreneur?.fundingNeeded || '$500K - $2M',
        previousExperience: existingEntrepreneur?.previousExperience || 'Experiencia en desarrollo de productos y gestión empresarial',
        businessModel: existingEntrepreneur?.businessModel || 'B2B SaaS con modelo de suscripción',
        teamSize: existingEntrepreneur?.teamSize || Math.floor(Math.random() * 20) + 3,
        achievements: existingEntrepreneur?.achievements || ['Producto validado en el mercado', 'Equipo sólido', 'Tracción inicial']
      } as Entrepreneur;
    } else {
      // Preserve existing investor data if editing
      const existingInvestor = initialUserData?.userType === 'investor' ? initialUserData as Investor : null;
      
      const investorTypes = ['angel', 'vc', 'corporate', 'family-office'] as const;
      const focusIndustries = [
        ['SaaS', 'Enterprise Software', 'B2B Tech'],
        ['FinTech', 'InsurTech', 'Financial Services'],
        ['HealthTech', 'MedTech', 'Digital Health'],
        ['CleanTech', 'Sustainability', 'Green Energy']
      ];
      
      const selectedFocus = focusIndustries[Math.floor(Math.random() * focusIndustries.length)];
      
      return {
        ...baseUser,
        userType: 'investor',
        investorType: existingInvestor?.investorType || investorTypes[Math.floor(Math.random() * investorTypes.length)],
        investmentRange: existingInvestor?.investmentRange || '$100K - $1M',
        focusIndustries: existingInvestor?.focusIndustries || selectedFocus,
        investmentStage: existingInvestor?.investmentStage || ['Seed', 'Series A'],
        portfolioSize: existingInvestor?.portfolioSize || Math.floor(Math.random() * 30) + 5,
        notableInvestments: existingInvestor?.notableInvestments || ['TechStartup', 'InnovateCorp', 'GrowthVenture'],
        expertise: existingInvestor?.expertise || ['Business Strategy', 'Market Development', 'Scaling Operations']
      } as Investor;
    }
  };

  /**
   * Handles form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));

      // Construct user profile
      const newUser = constructUserProfile(formData);
      
      console.log('👤 Usuario construido:', {
        id: newUser.id,
        name: newUser.name,
        userType: newUser.userType,
        hasImages: newUser.images.length > 0
      });
      
      // Intentar guardar en Supabase usando el servicio
      const supabaseResult = await SupabaseService.saveUser(newUser);
      
      if (supabaseResult.success) {
        console.log('🎉 ¡ÉXITO! Usuario guardado en Supabase correctamente');
      } else {
        console.error('❌ Error al guardar en Supabase:', supabaseResult.error);
        console.info('🔄 Continuando con almacenamiento local como respaldo');
        
        // Mostrar error al usuario pero no bloquear el flujo
        setErrors({ 
          name: `Advertencia: ${supabaseResult.error}. Los datos se guardaron localmente.` 
        });
        
        // Limpiar error después de 5 segundos
        setTimeout(() => {
          setErrors({});
        }, 5000);
      }

      // Save to localStorage for persistence
      console.log('💾 Guardando en localStorage como respaldo...');
      localStorage.setItem('racksMatchUserProfile', JSON.stringify(newUser));
      console.log('✅ Guardado en localStorage completado');
      
      // Call the callback function properly
      if (typeof onComplete === 'function') {
        onComplete(newUser);
      } else {
        console.error('❌ onComplete no es una función válida');
      }
    } catch (error) {
      console.error('❌ Error crítico al guardar perfil:', {
        error: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Mostrar error más específico al usuario
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Error inesperado. Por favor, inténtalo de nuevo.';
        
      setErrors({ name: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles step navigation
   */
  const nextStep = () => {
    if (currentStep === 1) {
      // Validate required fields before proceeding
      const nameError = validateField('name', formData.name);
      const emailError = validateField('email', formData.email);
      
      if (nameError || emailError) {
        setErrors({ name: nameError, email: emailError });
        return;
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, 2));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 overflow-y-auto scroll-container">
      <div className="w-full max-w-2xl">
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              
              {/* Logout button si está editando perfil */}
              {initialUserData && onLogout && (
                <div className="absolute top-4 right-4">
                  <Button
                    onClick={onLogout}
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Cerrar Sesión
                  </Button>
                </div>
              )}
              
              <Heading2>
                {initialUserData ? '¡Actualiza tu perfil!' : '¡Crea tu perfil profesional!'}
              </Heading2>
              <Body className="mt-2">
                {initialUserData 
                  ? 'Mantén tu información actualizada para mejores conexiones.'
                  : 'Conecta con emprendedores e inversores. Completa tu información para comenzar.'
                }
              </Body>
              
              {/* Progress Indicator */}
              <div className="flex items-center justify-center mt-6 space-x-2">
                <div className={`w-3 h-3 rounded-full ${currentStep >= 1 ? 'bg-blue-500' : 'bg-gray-300'}`} />
                <div className={`w-8 h-1 ${currentStep >= 2 ? 'bg-blue-500' : 'bg-gray-300'}`} />
                <div className={`w-3 h-3 rounded-full ${currentStep >= 2 ? 'bg-blue-500' : 'bg-gray-300'}`} />
              </div>
              <BodySmall className="mt-2 text-gray-600">
                Paso {currentStep} de 2
              </BodySmall>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Step 1: Basic Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Nombre completo *"
                      type="text"
                      placeholder="Tu nombre completo"
                      value={formData.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      error={errors.name}
                      icon={UserIcon}
                      required
                    />

                    <Input
                      label="Correo electrónico *"
                      type="email"
                      placeholder="tu@email.com"
                      value={formData.email}
                      onChange={(e) => handleFieldChange('email', e.target.value)}
                      error={errors.email}
                      icon={Mail}
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Ubicación"
                      type="text"
                      placeholder="Ciudad, País"
                      value={formData.location}
                      onChange={(e) => handleFieldChange('location', e.target.value)}
                      error={errors.location}
                      icon={MapPin}
                      hint="Ej: Madrid, España"
                    />

                    <Input
                      label="Teléfono"
                      type="tel"
                      placeholder="+34 600 000 000"
                      value={formData.phone}
                      onChange={(e) => handleFieldChange('phone', e.target.value)}
                      error={errors.phone}
                      icon={Phone}
                    />
                  </div>

                  <Input
                    label="Sitio web"
                    type="url"
                    placeholder="https://tu-sitio-web.com"
                    value={formData.website}
                    onChange={(e) => handleFieldChange('website', e.target.value)}
                    error={errors.website}
                    icon={Globe}
                    hint="URL completa incluyendo http:// o https://"
                  />

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={nextStep}
                      variant="primary"
                      size="lg"
                      disabled={!formData.name.trim() || !formData.email.trim() || !!errors.name || !!errors.email}
                    >
                      Continuar
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Profile Details & Image */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  {/* User Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Tipo de perfil *
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        onClick={() => handleUserTypeSelect('entrepreneur')}
                        className={`
                          p-6 rounded-xl border-2 cursor-pointer transition-all duration-200
                          ${selectedUserType === 'entrepreneur' 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                      >
                        <div className="text-center">
                          <div className={`
                            w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3
                            ${selectedUserType === 'entrepreneur' 
                              ? 'bg-green-500 text-white' 
                              : 'bg-gray-100 text-gray-600'
                            }
                          `}>
                            <Building2 className="w-6 h-6" />
                          </div>
                          <h3 className={`font-semibold mb-2 ${
                            selectedUserType === 'entrepreneur' ? 'text-green-700' : 'text-gray-700'
                          }`}>
                            Emprendedor
                          </h3>
                          <p className="text-sm text-gray-600">
                            Busco inversores y socios para hacer crecer mi startup
                          </p>
                        </div>
                      </div>

                      <div
                        onClick={() => handleUserTypeSelect('investor')}
                        className={`
                          p-6 rounded-xl border-2 cursor-pointer transition-all duration-200
                          ${selectedUserType === 'investor' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                      >
                        <div className="text-center">
                          <div className={`
                            w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3
                            ${selectedUserType === 'investor' 
                              ? 'bg-blue-500 text-white' 
                              : 'bg-gray-100 text-gray-600'
                            }
                          `}>
                            <TrendingUp className="w-6 h-6" />
                          </div>
                          <h3 className={`font-semibold mb-2 ${
                            selectedUserType === 'investor' ? 'text-blue-700' : 'text-gray-700'
                          }`}>
                            Inversor
                          </h3>
                          <p className="text-sm text-gray-600">
                            Busco startups prometedoras para invertir y asesorar
                          </p>
                        </div>
                      </div>
                    </div>
                    {errors.userType && (
                      <p className="text-sm text-red-600 mt-2">{errors.userType}</p>
                    )}
                  </div>
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Foto de perfil
                    </label>
                    <ImageUpload
                      onImageSelect={handleImageSelect}
                      onImageRemove={handleImageRemove}
                      currentImage={formData.profileImage}
                      isUploading={isImageUploading}
                      uploadProgress={imageUploadProgress}
                      maxSizeBytes={5 * 1024 * 1024} // 5MB
                      acceptedFormats={['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']}
                    />
                    {formData.profileImage && formData.profileImage.includes('supabase') && (
                      <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                        <span>✅</span>
                        Imagen guardada en Supabase Storage
                      </p>
                    )}
                  </div>

                  {/* Bio */}
                  <div>
                    <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                      Biografía profesional
                    </label>
                    <div className="relative">
                      <textarea
                        id="bio"
                        rows={4}
                        placeholder="Cuéntanos sobre tu experiencia profesional, objetivos y qué buscas en RacksMatch..."
                        value={formData.bio}
                        onChange={(e) => handleFieldChange('bio', e.target.value)}
                        className={`
                          w-full px-4 py-3 text-sm text-gray-900 rounded-lg border
                          ${errors.bio ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                          placeholder:text-gray-500 resize-none
                        `}
                        maxLength={500}
                      />
                      <FileText className="absolute top-3 right-3 w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      {errors.bio && (
                        <p className="text-sm text-red-600">{errors.bio}</p>
                      )}
                      <p className="text-xs text-gray-500 ml-auto">
                        {formData.bio.length}/500 caracteres
                      </p>
                    </div>
                  </div>


                  {/* Navigation Buttons */}
                  <div className="flex justify-between">
                    <Button
                      type="button"
                      onClick={prevStep}
                      variant="secondary"
                      size="lg"
                    >
                      Atrás
                    </Button>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      loading={isSubmitting}
                      disabled={!selectedUserType}
                      icon={Save}
                    >
                      {isSubmitting 
                        ? (initialUserData ? 'Actualizando...' : 'Creando perfil...') 
                        : (initialUserData ? 'Guardar cambios' : 'Crear mi perfil')
                      }
                    </Button>
                  </div>
                </div>
              )}
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-100 text-center">
              <BodySmall className="text-gray-600">
                Al continuar, aceptas nuestros{' '}
                <a href="#" className="text-blue-600 hover:text-blue-700 underline">
                  términos de servicio
                </a>{' '}
                y{' '}
                <a href="#" className="text-blue-600 hover:text-blue-700 underline">
                  política de privacidad
                </a>
                .
              </BodySmall>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};